<template>
    <div class="notifications">
        <page-header
            class="notifications__header"
            :filter-disabled="true"
            @search="onPatientSearch"
        />
        <div class="notifications__content">
            <div class="notifications__title-row">
                <back-button
                    v-if="showBackButton"
                    @click="goBack"
                />
                <h1>
                    Уведомления
                </h1>
            </div>
            <a-table
                :columns="columns"
                :data-source="notifications"
                :pagination="false"
                :loading="loading"
                :show-header="false"
                size="small"
                row-key="rowKey"
            >
                <template #icon="{ record }">
                    <reconciliation-two-tone
                        v-if="record.entityName === 'goodSurveysReadyNew'"
                        two-tone-color="#f5222d"
                    />
                    <close-square-two-tone
                        v-else-if="record.entityName === 'patientTask'"
                        two-tone-color="#f5222d"
                    />
                    <bell-two-tone
                        v-else
                        two-tone-color="#f5222d"
                    />
                </template>
                <template #text="{ record }">
                    <a-button
                        v-if="record.entityName === 'protocolModification'
                            || record.entityName === 'personalNotification'"
                        size="small"
                        class="clear-btn"
                        type="text"
                        @click="onDeleteNotification(record.id, record.entityName)"
                    >
                        <check-square-two-tone
                            two-tone-color="#1da929"
                        />
                    </a-button>
                    <span
                        :class="{
                            'notification__content_item-text':
                                record.entityName !== 'personalNotification',
                        }"
                        @click="onNotificationClick(record)"
                    >
                        {{ getNotificationText(record) }}
                    </span>
                </template>
            </a-table>
            <a-pagination
                v-if="pagination.total > pagination.pageSize"
                :current="pagination.current"
                :total="pagination.total"
                :show-total="(total, range) => `${range[0]}-${range[1]} из ${total}`"
                :page-size="pagination.pageSize"
                size="small"
                class="notifications__pagination"
                @change="onPaginationChange"
            />
        </div>
    </div>
</template>

<script>
import ruRU from 'ant-design-vue/lib/locale-provider/ru_RU';
import ReconciliationTwoTone from '@ant-design/icons-vue/ReconciliationTwoTone';
import CloseSquareTwoTone from '@ant-design/icons-vue/CloseSquareTwoTone';
import BellTwoTone from '@ant-design/icons-vue/BellTwoTone';
import CheckSquareTwoTone from '@ant-design/icons-vue/CheckSquareTwoTone';

import PageHeader from '@/components/PageHeader.vue';
import BackButton from '@/components/BackButton.vue';
import useBackButton from '@/composables/useBackButton';
import {
    PAGE_NAMES,
    consumePendingRestoreContext,
} from '@/composables/useBackState';
import { restoreBackContext } from '@/utils/backContextRestore';
import { savePageBackState } from '@/utils/backNavigation';
import { showErrorNotification, sseService } from '@/utils';

const PAGE_SIZE = 20;
export default {
    components: {
        BackButton,
        PageHeader,
        ReconciliationTwoTone,
        CloseSquareTwoTone,
        BellTwoTone,
        CheckSquareTwoTone,
    },

    setup() {
        const { goBack, showBackButton } = useBackButton();

        return {
            goBack,
            showBackButton,
        };
    },

    data() {
        return {
            ruRU,

            notifications: [],
            loading: false,
            pagination: {
                pageSize: PAGE_SIZE,
                current: 1,
                total: 0,
            },
            patientQuery: '',
            unsubscribeNotification: null,

            columns: [{
                width: 40,
                align: 'center',
                slots: { customRender: 'icon' },
            }, {
                slots: { customRender: 'text' },
            }],
        };
    },

    watch: {
        patientQuery: 'fetchNotifications',
    },

    mounted() {
        const pending = consumePendingRestoreContext();

        restoreBackContext(pending, {
            [PAGE_NAMES.NOTIFICATIONS]: (pageData) => this.restoreNotificationsContext(pageData),
        });

        this.fetchNotifications();

        this.unsubscribeNotification = sseService.subscribe(
            'notification',
            this.onSseNotification,
        );
    },

    beforeUnmount() {
        if (typeof this.unsubscribeNotification === 'function') {
            this.unsubscribeNotification();
        }
        this.unsubscribeNotification = null;
    },

    methods: {
        restoreNotificationsContext(pageData) {
            if (typeof pageData.paginationCurrent === 'number') {
                this.pagination.current = pageData.paginationCurrent;
            }

            if (typeof pageData.patientQuery === 'string') {
                this.patientQuery = pageData.patientQuery;
            }
        },

        buildPageBackData() {
            return {
                paginationCurrent: this.pagination.current,
                patientQuery: this.patientQuery,
            };
        },

        async fetchNotifications() {
            const { current, pageSize } = this.pagination;
            const params = {
                page: JSON.stringify([current - 1, pageSize]),
                query: this.patientQuery,
            };

            this.loading = true;
            let response;
            try {
                response = await this.$http.get('/patient/doctor/notifications', {
                    params,
                });
            } catch (err) {
                return;
            } finally {
                this.loading = false;
            }

            const { data, count } = response.data;
            this.notifications = data.map((record) => ({
                ...record,
                rowKey: `${record.entityName}-${record.id}`,
            }));

            const totalPages = Math.ceil(count / this.pagination.pageSize) || 1;
            const activePage = this.pagination.current > totalPages
                ? totalPages
                : this.pagination.current;
            this.pagination.current = activePage;
            this.pagination.total = count;
        },

        getNotificationText(record) {
            switch (record.entityName) {
            case 'goodSurveysReadyNew':
                return `${record.eventDate} - ${record.patientName.trim()}. ${record.eventName}`;
            case 'patientTask':
                return `${record.patientName.trim()}. ${record.eventName}`;
            case 'protocolModification':
            case 'personalNotification':
                return `${record.eventDate} - ${record.eventName}`;
            default:
                return record.eventName;
            }
        },

        onPatientSearch({ search }) {
            this.patientQuery = search;
        },

        async onDeleteNotification(notificationId, entityName) {
            try {
                await this.$http.delete(
                    `/patient/doctor/notification/${
                        notificationId}?entityName=${entityName}`,
                );
                this.notifications = this.notifications
                    .filter((n) => notificationId !== n.id);
            } catch {
                showErrorNotification('Не удалось удалить уведомление.');
            }
        },

        onPaginationChange(page) {
            this.pagination.current = page;
            this.fetchNotifications();
        },

        onNotificationClick(record) {
            if (record.entityName === 'personalNotification') {
                return;
            }

            savePageBackState(
                this.$route.fullPath || '/notifications',
                PAGE_NAMES.NOTIFICATIONS,
                this.buildPageBackData(),
            );

            switch (record.entityName) {
            case 'goodSurveysReadyNew':
                this.$router.push({
                    path: `/good-surveys/${record.id}`,
                });
                break;
            case 'protocolModification':
            case 'patientTask':
                this.$router.push({
                    path: `/reception/${record.id}`,
                });
                break;
            default:
                break;
            }
        },

        onSseNotification() {
            if (this.pagination.current === 1) {
                this.fetchNotifications();
            }
        },
    },
};
</script>

<style lang="scss">
    .notifications {
        display: grid;
        height: 100vh;
        gap: 10px;
        grid:
            "header" 64px
            "content" calc(100% - 74px);
        .clear-btn {
            margin-right: 20px;
        }
    }

    .notifications__header {
        grid-area: header;
    }

    .notifications__content {
        grid-area: content;
        text-align: left;
        margin: 10px;
    }

    .notifications__title-row {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;

        h1 {
            margin: 0;
            line-height: 32px;
        }
    }

    .notifications__pagination {
        &.ant-pagination {
            padding: 10px;
        }

        .ant-pagination-prev .ant-pagination-item-link::after,
        .ant-pagination-next .ant-pagination-item-link::after,
        .ant-pagination-jump-next:hover::after,
        .ant-pagination-jump-next::after,
        .ant-pagination-jump-prev:hover::after,
        .ant-pagination-jump-prev::after {
            content: none;
        }
    }

    .notification__content_item-text {
        cursor: pointer;
    }
</style>
